import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Typography,
  Tabs,
  Tab,
  Alert,
  Chip,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import VisibilityIcon from '@mui/icons-material/Visibility';
import axios from '../../api/axios';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';

const PagesManager = () => {
  const [pages, setPages] = useState([]);
  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [tabValue, setTabValue] = useState(0);
  const [formData, setFormData] = useState({
    slug: '',
    title: '',
    content: '',
    description: '',
    metaKeywords: '',
    order: 0,
  });

  // Настройки для React Quill
  const modules = {
    toolbar: [
      [{ 'header': [1, 2, 3, 4, 5, 6, false] }],
      [{ 'font': [] }],
      [{ 'size': ['small', false, 'large', 'huge'] }],
      ['bold', 'italic', 'underline', 'strike'],
      [{ 'color': [] }, { 'background': [] }],
      [{ 'script': 'sub'}, { 'script': 'super' }],
      [{ 'list': 'ordered'}, { 'list': 'bullet' }],
      [{ 'indent': '-1'}, { 'indent': '+1' }],
      [{ 'align': [] }],
      ['blockquote', 'code-block'],
      ['link', 'image', 'video'],
      ['clean']
    ],
  };

  const formats = [
    'header', 'font', 'size',
    'bold', 'italic', 'underline', 'strike',
    'color', 'background',
    'script',
    'list', 'bullet', 'indent',
    'align',
    'blockquote', 'code-block',
    'link', 'image', 'video'
  ];

  useEffect(() => {
    fetchPages();
  }, []);

  const fetchPages = async () => {
    try {
      const response = await axios.get('/pages');
      setPages(response.data);
    } catch (error) {
      console.error('Ошибка загрузки страниц:', error);
    }
  };

  const handleOpen = (page = null) => {
    if (page) {
      setEditingId(page._id);
      setFormData(page);
    } else {
      setEditingId(null);
      setFormData({
        slug: '',
        title: '',
        content: '',
        description: '',
        metaKeywords: '',
        order: 0,
      });
    }
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setEditingId(null);
    setTabValue(0);
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleContentChange = (content) => {
    setFormData({
      ...formData,
      content: content,
    });
  };

  const handleSubmit = async () => {
    try {
      if (editingId) {
        await axios.put(`/pages/${editingId}`, formData);
      } else {
        await axios.post('/pages', formData);
      }
      fetchPages();
      handleClose();
    } catch (error) {
      console.error('Ошибка сохранения страницы:', error);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Вы уверены, что хотите удалить эту страницу?')) {
      try {
        await axios.delete(`/pages/${id}`);
        fetchPages();
      } catch (error) {
        console.error('Ошибка удаления страницы:', error);
      }
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
        <Typography variant="h5">Управление страницами</Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleOpen()}
        >
          Добавить страницу
        </Button>
      </Box>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Заголовок</TableCell>
              <TableCell>Slug</TableCell>
              <TableCell>Порядок</TableCell>
              <TableCell>URL</TableCell>
              <TableCell align="right">Действия</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {pages.map((page) => (
              <TableRow key={page._id}>
                <TableCell>{page.title}</TableCell>
                <TableCell>
                  <Chip label={page.slug} size="small" color="primary" variant="outlined" />
                </TableCell>
                <TableCell>{page.order}</TableCell>
                <TableCell>
                  <Typography variant="body2" color="text.secondary">
                    /page/{page.slug}
                  </Typography>
                </TableCell>
                <TableCell align="right">
                  <IconButton 
                    color="info" 
                    onClick={() => window.open(`/page/${page.slug}`, '_blank')}
                    title="Посмотреть на сайте"
                  >
                    <VisibilityIcon />
                  </IconButton>
                  <IconButton color="primary" onClick={() => handleOpen(page)}>
                    <EditIcon />
                  </IconButton>
                  <IconButton
                    color="error"
                    onClick={() => handleDelete(page._id)}
                  >
                    <DeleteIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={open} onClose={handleClose} maxWidth="lg" fullWidth>
        <DialogTitle>
          {editingId ? 'Редактировать страницу' : 'Добавить страницу'}
        </DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mt: 2, mb: 2 }}>
            💡 Используйте визуальный редактор для форматирования текста, добавления изображений и создания красивых страниц!
          </Alert>

          <TextField
            fullWidth
            label="Заголовок"
            name="title"
            value={formData.title}
            onChange={handleChange}
            margin="normal"
            required
          />
          <TextField
            fullWidth
            label="Slug (для URL)"
            name="slug"
            value={formData.slug}
            onChange={handleChange}
            margin="normal"
            required
            helperText={`Страница будет доступна по адресу: /page/${formData.slug || 'your-slug'}`}
          />

          <Box sx={{ borderBottom: 1, borderColor: 'divider', mt: 3, mb: 2 }}>
            <Tabs value={tabValue} onChange={(e, newValue) => setTabValue(newValue)}>
              <Tab label="✏️ Редактировать" />
              <Tab label="👁️ Предпросмотр" />
            </Tabs>
          </Box>

          {tabValue === 0 ? (
            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle2" gutterBottom>
                Содержание страницы
              </Typography>
              <Box sx={{ 
                border: '1px solid #e0e0e0', 
                borderRadius: 1,
                '& .ql-container': {
                  minHeight: '300px',
                  fontSize: '16px',
                },
                '& .ql-editor': {
                  minHeight: '300px',
                }
              }}>
                <ReactQuill
                  theme="snow"
                  value={formData.content}
                  onChange={handleContentChange}
                  modules={modules}
                  formats={formats}
                  placeholder="Начните писать содержание страницы..."
                />
              </Box>
            </Box>
          ) : (
            <Box sx={{ 
              mt: 2, 
              p: 3, 
              border: '1px solid #e0e0e0', 
              borderRadius: 1,
              minHeight: '300px',
              backgroundColor: '#f9f9f9'
            }}>
              <Typography variant="h4" gutterBottom>
                {formData.title || 'Заголовок страницы'}
              </Typography>
              <Box 
                sx={{ 
                  '& img': { maxWidth: '100%', height: 'auto' },
                  '& a': { color: 'primary.main' }
                }}
                dangerouslySetInnerHTML={{ __html: formData.content || '<p>Нет содержания</p>' }}
              />
            </Box>
          )}

          <TextField
            fullWidth
            label="Описание (meta)"
            name="description"
            value={formData.description}
            onChange={handleChange}
            margin="normal"
            multiline
            rows={2}
            helperText="Краткое описание страницы для поисковых систем"
          />
          <TextField
            fullWidth
            label="Ключевые слова (meta)"
            name="metaKeywords"
            value={formData.metaKeywords}
            onChange={handleChange}
            margin="normal"
            helperText="Через запятую: например, консультация, помощь, родители"
          />
          <TextField
            fullWidth
            label="Порядок отображения"
            name="order"
            type="number"
            value={formData.order}
            onChange={handleChange}
            margin="normal"
            helperText="Чем меньше число, тем выше в списке"
          />
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={handleClose}>Отмена</Button>
          {formData.slug && (
            <Button 
              onClick={() => window.open(`/page/${formData.slug}`, '_blank')}
              startIcon={<VisibilityIcon />}
            >
              Посмотреть на сайте
            </Button>
          )}
          <Button onClick={handleSubmit} variant="contained">
            Сохранить
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PagesManager;
